import { StyleSheet } from '@react-pdf/renderer';

// Common date formatting
export const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format tag names (remove underscores, capitalize)
export const formatTagName = (tag) => {
  return tag
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format key names (remove trailing numbers, capitalize)
export const formatKeyName = (key) => {
  // Handle seed question keys
  if (key.startsWith('sq_conv_')) {
    const match = key.match(/_(\d+)$/);
    if (match) {
      return `Seed Question ${parseInt(match[1]) + 1}`;
    }
    return 'Seed Question';
  }
  
  return key
    .replace(/_\d+$/, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Parse values to add spaces for readability
export const parseValue = (value) => {
  if (!value) return value;
  
  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  
  // Handle objects (not JSON strings)
  if (typeof value === 'object' && value !== null) {
    // Handle interview introduction format
    if (value.introduction_text) {
      return { type: 'interview_intro', data: value };
    }
    // Handle badge format
    if (value.badge && value.description) {
      return { type: 'interview_badge', data: value };
    }
    // Fallback for other objects
    return JSON.stringify(value);
  }
  
  let strValue = String(value);
  
  // Try to parse JSON strings (for seed questions and interview data)
  if (strValue.startsWith('{') && strValue.endsWith('}')) {
    try {
      const parsed = JSON.parse(strValue);
      if (parsed.question) {
        // Return as an object to format specially in the PDF
        return { type: 'seed_question', data: parsed };
      }
      if (parsed.introduction_text) {
        return { type: 'interview_intro', data: parsed };
      }
      if (parsed.badge && parsed.description) {
        return { type: 'interview_badge', data: parsed };
      }
    } catch (e) {
      // If parsing fails, continue with normal processing
    }
  }
  
  // Handle slash-separated values
  if (strValue.includes('/')) {
    strValue = strValue.split('/').map(part => part.trim()).join(' ');
  }
  
  // Handle camelCase or PascalCase
  strValue = strValue.replace(/([a-z])([A-Z])/g, '$1 $2');
  strValue = strValue.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  
  return strValue;
};

// Common PDF styles
export const commonPDFStyles = {
  page: {
    paddingTop: 40,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 80, // Extra space for footer + margin
    fontFamily: 'Helvetica',
    fontSize: 11,
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 30,
    borderBottom: '2pt solid #e5e7eb',
    paddingBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginBottom: 5,
  },
  section: {
    marginTop: 25,
    backgroundColor: '#f9fafb',
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 20,
    paddingRight: 15,
    borderRadius: 8,
    borderLeft: '4pt solid #9ca3af',
    breakInside: 'avoid',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#374151',
    borderBottom: '1pt solid #e5e7eb',
    paddingBottom: 8,
  },
  itemContainer: {
    marginBottom: 6,
  },
  itemLabel: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 2,
    fontWeight: 'bold',
  },
  itemValue: {
    fontSize: 11,
    color: '#1f2937',
    marginBottom: 4,
  },
  footerText: {
    position: 'absolute',
    fontSize: 9,
    bottom: 30,
    left: 0,
    right: 0,
    color: '#374151', // temporarily darken from #9ca3af
    textAlign: 'center',
  },
  emptyState: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 40,
  },
  // Seed question specific styles
  seedQuestionContainer: {
    marginBottom: 12,
    backgroundColor: '#f3f4f6',
    padding: 10,
    paddingLeft: 15,
    borderRadius: 6,
    borderLeft: '3pt solid #6b7280',
  },
  questionLabel: {
    fontSize: 10,
    color: '#374151',
    fontWeight: 'bold',
    marginBottom: 2,
  },
  questionText: {
    fontSize: 11,
    color: '#1f2937',
    marginBottom: 8,
    lineHeight: 1.5,
  },
  rationaleText: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 6,
    lineHeight: 1.4,
  },
  objectiveText: {
    fontSize: 10,
    color: '#6b7280',
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
};

// Create a complete StyleSheet from common styles
export const createCommonStyles = (additionalStyles = {}) => {
  return StyleSheet.create({
    ...commonPDFStyles,
    ...additionalStyles
  });
};